import { ArrowDownIcon, ArrowUpIcon, DeleteIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Center,
    Checkbox,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import {
    createBsCoc,
    deleteBsCoc,
    getAllBsClients,
    getAllBsCocs,
    getAllBsSampleSizeCharts,
    getAllBsSampleTypes,
    getAllBsTestingTypes,
    restoreBsCoc,
    updateBsCoc
} from '../../BSApi';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import PopoverId from '../../components/PopoverId';
import TitleHeader from '../../components/TitleHeader';
import { hasPermission, useTableColors } from '../../Utils';

const STATUS_OPTIONS = ['draft', 'completed', 'submitted', 'cancelled'];

const emptyItem = () => ({
    itemName: '',
    batchNumber: '',
    batchType: '',
    sampleTypeId: '',
    batchSize: 0,
    batchUnit: '',
    sampleSize: 0,
    sampleUnit: '',
    testingTypeId: '',
    notes: ''
});

const emptySignatureBlock = () => ({
    signature: '',
    name: '',
    date: ''
});

const emptyForm = () => ({
    cocNumber: '',
    clientId: '',
    receivedDate: '',
    collectedDate: '',
    startingDate: '',
    startingTime: '',
    endingTime: '',
    cash: 0,
    cashReceivedBy: '',
    cashNotes: '',
    status: 'draft',
    items: [emptyItem()],
    courierSignatureBlock: emptySignatureBlock(),
    clientSignatureBlock: emptySignatureBlock(),
    notes: ''
});

const applyRounding = (value, mode) => {
    const numericValue = Number(value) || 0;
    switch (mode) {
        case 'up':
            return Math.ceil(numericValue);
        case 'down':
            return Math.floor(numericValue);
        case 'nearest':
            return Math.round(numericValue);
        case 'none':
        default:
            return numericValue;
    }
};

const BSSCOCsPage = () => {
    const coolors = useTableColors();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [sampleTypes, setSampleTypes] = useState([]);
    const [sampleSizeCharts, setSampleSizeCharts] = useState([]);
    const [testingTypes, setTestingTypes] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [newItem, setNewItem] = useState(emptyForm());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleted, setShowDeleted] = useState(false);
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    useEffect(() => { loadData(); }, [showDeleted]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [cocsRes, clientsRes, sampleTypesRes, sampleSizeChartsRes, testingTypesRes] = await Promise.all([
                getAllBsCocs(showDeleted),
                getAllBsClients(false),
                getAllBsSampleTypes(false),
                getAllBsSampleSizeCharts(false),
                getAllBsTestingTypes(false)
            ]);

            if (!cocsRes.success) throw new Error(cocsRes.error || 'Failed to load COCs');
            if (!clientsRes.success) throw new Error(clientsRes.error || 'Failed to load clients');
            if (!sampleTypesRes.success) throw new Error(sampleTypesRes.error || 'Failed to load sample types');
            if (!sampleSizeChartsRes.success) throw new Error(sampleSizeChartsRes.error || 'Failed to load sample size charts');
            if (!testingTypesRes.success) throw new Error(testingTypesRes.error || 'Failed to load testing types');

            setItems(Array.isArray(cocsRes.data) ? cocsRes.data : []);
            setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
            setSampleTypes(Array.isArray(sampleTypesRes.data) ? sampleTypesRes.data : []);
            setSampleSizeCharts(Array.isArray(sampleSizeChartsRes.data) ? sampleSizeChartsRes.data : []);
            setTestingTypes(Array.isArray(testingTypesRes.data) ? testingTypesRes.data : []);
        } catch (err) {
            setError(err.message || 'Failed to load BSS COCs');
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return [...items]
            .filter((item) => {
                const clientName = item?.clientId?.clientName || '';
                return !term || [item.cocNumber, clientName, item.status].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));
            })
            .sort((a, b) => {
                const aValue = sortField === 'clientName' ? a?.clientId?.clientName || '' : a?.[sortField] ?? '';
                const bValue = sortField === 'clientName' ? b?.clientId?.clientName || '' : b?.[sortField] ?? '';
                const result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
                return sortOrder === 'asc' ? result : -result;
            });
    }, [items, searchTerm, sortField, sortOrder]);

    const toggleSort = (field) => {
        const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newOrder);
    };

    const handleChange = (setter, field, value) => setter((prev) => ({ ...prev, [field]: value }));
    const handleNestedChange = (setter, parent, field, value) => setter((prev) => ({ ...prev, [parent]: { ...(prev[parent] || {}), [field]: value } }));

    const getMatchingChart = (sampleTypeId, batchSize) => {
        if (!sampleTypeId || batchSize === '' || batchSize === null || batchSize === undefined) return null;
        const numericBatchSize = Number(batchSize);
        if (Number.isNaN(numericBatchSize)) return null;

        return sampleSizeCharts.find((chart) => {
            const chartSampleTypeId = chart?.sampleTypeId?._id || chart?.sampleTypeId;
            return (
                chart?.isActive !== false &&
                String(chartSampleTypeId) === String(sampleTypeId) &&
                numericBatchSize >= Number(chart.minBatchSize) &&
                numericBatchSize <= Number(chart.maxBatchSize)
            );
        }) || null;
    };

    const getSampleTypeCharts = (sampleTypeId) => {
        if (!sampleTypeId) return [];
        return sampleSizeCharts.filter((chart) => {
            const chartSampleTypeId = chart?.sampleTypeId?._id || chart?.sampleTypeId;
            return chart?.isActive !== false && String(chartSampleTypeId) === String(sampleTypeId);
        });
    };

    const buildUpdatedItemFromChart = (currentItem, field, value) => {
        const updatedItem = { ...currentItem, [field]: value };
        const availableCharts = getSampleTypeCharts(updatedItem.sampleTypeId);

        if (field === 'sampleTypeId') {
            if (availableCharts.length > 0) {
                updatedItem.batchUnit = availableCharts[0].batchUnit || '';
                updatedItem.sampleUnit = availableCharts[0].sampleUnit || '';
            } else {
                updatedItem.batchUnit = '';
                updatedItem.sampleUnit = '';
                updatedItem.sampleSize = 0;
            }
        }

        const matchingChart = getMatchingChart(updatedItem.sampleTypeId, updatedItem.batchSize);

        if (matchingChart) {
            updatedItem.batchUnit = matchingChart.batchUnit || '';
            updatedItem.sampleUnit = matchingChart.sampleUnit || '';
            updatedItem.sampleSize = applyRounding(matchingChart.requiredSampleSize, matchingChart.roundingMode);
        } else if (field === 'batchSize') {
            updatedItem.sampleSize = 0;
        }

        return updatedItem;
    };

    const handleItemChange = (setter, index, field, value) => {
        setter((prev) => ({
            ...prev,
            items: (prev.items || []).map((item, i) => {
                if (i !== index) return item;
                return buildUpdatedItemFromChart(item, field, value);
            })
        }));
    };

    const addFormItem = (setter) => setter((prev) => ({ ...prev, items: [...(prev.items || []), emptyItem()] }));
    const removeFormItem = (setter, index) => setter((prev) => ({ ...prev, items: (prev.items || []).filter((_, i) => i !== index).length ? (prev.items || []).filter((_, i) => i !== index) : [emptyItem()] }));

    const openEdit = (item) => {
        setSelectedItem({
            ...item,
            clientId: item?.clientId?._id || item?.clientId || '',
            receivedDate: item?.receivedDate ? String(item.receivedDate).slice(0, 10) : '',
            collectedDate: item?.collectedDate ? String(item.collectedDate).slice(0, 10) : '',
            startingDate: item?.startingDate ? String(item.startingDate).slice(0, 10) : '',
            items: (item.items || []).map((sub) => ({
                ...sub,
                sampleTypeId: sub?.sampleTypeId?._id || sub?.sampleTypeId || '',
                testingTypeId: sub?.testingTypeId?._id || sub?.testingTypeId || ''
            })),
            courierSignatureBlock: {
                signature: item?.courierSignatureBlock?.signature || '',
                name: item?.courierSignatureBlock?.name || '',
                date: item?.courierSignatureBlock?.date ? String(item.courierSignatureBlock.date).slice(0, 10) : ''
            },
            clientSignatureBlock: {
                signature: item?.clientSignatureBlock?.signature || '',
                name: item?.clientSignatureBlock?.name || '',
                date: item?.clientSignatureBlock?.date ? String(item.clientSignatureBlock.date).slice(0, 10) : ''
            }
        });
        onOpen();
    };

    const openDelete = (item) => { setSelectedItem(item); onDeleteOpen(); };

    const normalizePayload = (item) => ({
        ...item,
        cash: Number(item.cash) || 0,
        items: (item.items || []).map((sub) => ({
            ...sub,
            batchSize: Number(sub.batchSize) || 0,
            sampleSize: Number(sub.sampleSize) || 0
        }))
    });

    const handleCreate = async () => {
        const response = await createBsCoc(normalizePayload(newItem));
        if (response.success) {
            setNewItem(emptyForm());
            onCreateClose();
            loadData();
        } else alert(response.error || 'Failed to create COC');
    };

    const handleSave = async () => {
        const response = await updateBsCoc(selectedItem._id, normalizePayload(selectedItem));
        if (response.success) {
            onClose();
            loadData();
        } else alert(response.error || 'Failed to update COC');
    };

    const handleDelete = async () => {
        const response = await deleteBsCoc(selectedItem._id);
        if (response.success) {
            onDeleteClose();
            loadData();
        } else alert(response.error || 'Failed to delete COC');
    };

    const handleRestore = async (id) => {
        const response = await restoreBsCoc(id);
        if (response.success) loadData();
        else alert(response.error || 'Failed to restore COC');
    };

    const renderItemsForm = (formState, setter) => (
        <VStack spacing={4} align="stretch" w="full">
            {(formState.items || []).map((subItem, index) => {
                const matchingChart = getMatchingChart(subItem.sampleTypeId, subItem.batchSize);
                const hasManualSampleFallback = !!(subItem.sampleTypeId && Number(subItem.batchSize) > 0 && !matchingChart);
                return (
                    <Box key={index} p={4} borderWidth="1px" borderRadius="md">
                        <HStack justify="space-between" mb={3}>
                            <Text fontWeight="bold">COC Item #{index + 1}</Text>
                            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeFormItem(setter, index)}>Remove</Button>
                        </HStack>
                        <VStack spacing={3}>
                            <HStack w="full" spacing={4} align="start">
                                <FormControl><FormLabel>Item Name</FormLabel><Input value={subItem.itemName || ''} onChange={(e) => handleItemChange(setter, index, 'itemName', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Batch Number</FormLabel><Input value={subItem.batchNumber || ''} onChange={(e) => handleItemChange(setter, index, 'batchNumber', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Batch Type</FormLabel><Input value={subItem.batchType || ''} onChange={(e) => handleItemChange(setter, index, 'batchType', e.target.value)} /></FormControl>
                            </HStack>
                            <HStack w="full" spacing={4} align="start">
                                <FormControl>
                                    <FormLabel>Sample Type</FormLabel>
                                    <Select value={subItem.sampleTypeId || ''} onChange={(e) => handleItemChange(setter, index, 'sampleTypeId', e.target.value)}>
                                        <option value="">Select sample type</option>
                                        {sampleTypes.map((type) => <option key={type._id} value={type._id}>{type.name}</option>)}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Testing Type</FormLabel>
                                    <Select value={subItem.testingTypeId || ''} onChange={(e) => handleItemChange(setter, index, 'testingTypeId', e.target.value)}>
                                        <option value="">Select testing type</option>
                                        {testingTypes.map((type) => <option key={type._id} value={type._id}>{type.name}</option>)}
                                    </Select>
                                </FormControl>
                            </HStack>
                            <HStack w="full" spacing={4} align="start">
                                <FormControl>
                                    <FormLabel>Batch Size</FormLabel>
                                    <Input type="number" value={subItem.batchSize ?? 0} onChange={(e) => handleItemChange(setter, index, 'batchSize', e.target.value)} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Batch Unit</FormLabel>
                                    <Input
                                        value={subItem.batchUnit || ''}
                                        isReadOnly={!hasManualSampleFallback}
                                        placeholder={hasManualSampleFallback ? "Enter batch unit" : "Auto from chart"}
                                        onChange={(e) => handleItemChange(setter, index, 'batchUnit', e.target.value)}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Sample Size</FormLabel>
                                    <Input
                                        type="number"
                                        value={subItem.sampleSize ?? 0}
                                        isReadOnly={!hasManualSampleFallback}
                                        placeholder={hasManualSampleFallback ? "Enter sample size" : "Auto calculated"}
                                        onChange={(e) => handleItemChange(setter, index, 'sampleSize', e.target.value)}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Sample Unit</FormLabel>
                                    <Input
                                        value={subItem.sampleUnit || ''}
                                        isReadOnly={!hasManualSampleFallback}
                                        placeholder={hasManualSampleFallback ? "Enter sample unit" : "Auto from chart"}
                                        onChange={(e) => handleItemChange(setter, index, 'sampleUnit', e.target.value)}
                                    />
                                </FormControl>
                            </HStack>
                            <FormControl>
                                <FormLabel>Item Notes</FormLabel>
                                <Textarea value={subItem.notes || ''} onChange={(e) => handleItemChange(setter, index, 'notes', e.target.value)} />
                                {subItem.sampleTypeId && Number(subItem.batchSize) > 0 && !matchingChart && (
                                    <FormHelperText color="orange.500">No active sample size chart matches this sample type and batch size. You can enter Batch Unit, Sample Size, and Sample Unit manually.</FormHelperText>
                                )}
                            </FormControl>
                        </VStack>
                    </Box>
                );
            })}
            <Button size="sm" onClick={() => addFormItem(setter)}>Add Item</Button>
        </VStack>
    );

    const renderForm = (formState, setter) => (
        <VStack spacing={4} align="stretch">
            <HStack w="full" spacing={4} align="start">
                <FormControl><FormLabel>COC Number</FormLabel><Input value={formState.cocNumber || ''} onChange={(e) => handleChange(setter, 'cocNumber', e.target.value)} /></FormControl>
                <FormControl>
                    <FormLabel>Client</FormLabel>
                    <Select value={formState.clientId || ''} onChange={(e) => handleChange(setter, 'clientId', e.target.value)}>
                        <option value="">Select client</option>
                        {clients.map((client) => <option key={client._id} value={client._id}>{client.clientName}</option>)}
                    </Select>
                </FormControl>
                <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Select value={formState.status || 'draft'} onChange={(e) => handleChange(setter, 'status', e.target.value)}>
                        {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                    </Select>
                </FormControl>
            </HStack>
            <HStack w="full" spacing={4} align="start">
                <FormControl><FormLabel>Received Date</FormLabel><Input type="date" value={formState.receivedDate || ''} onChange={(e) => handleChange(setter, 'receivedDate', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Collected Date</FormLabel><Input type="date" value={formState.collectedDate || ''} onChange={(e) => handleChange(setter, 'collectedDate', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Starting Date</FormLabel><Input type="date" value={formState.startingDate || ''} onChange={(e) => handleChange(setter, 'startingDate', e.target.value)} /></FormControl>
            </HStack>
            <HStack w="full" spacing={4} align="start">
                <FormControl><FormLabel>Starting Time</FormLabel><Input type="time" value={formState.startingTime || ''} onChange={(e) => handleChange(setter, 'startingTime', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Ending Time</FormLabel><Input type="time" value={formState.endingTime || ''} onChange={(e) => handleChange(setter, 'endingTime', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Cash</FormLabel><Input type="number" value={formState.cash ?? 0} onChange={(e) => handleChange(setter, 'cash', e.target.value)} /></FormControl>
            </HStack>
            <HStack w="full" spacing={4} align="start">
                <FormControl><FormLabel>Cash Received By</FormLabel><Input value={formState.cashReceivedBy || ''} onChange={(e) => handleChange(setter, 'cashReceivedBy', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Cash Notes</FormLabel><Input value={formState.cashNotes || ''} onChange={(e) => handleChange(setter, 'cashNotes', e.target.value)} /></FormControl>
            </HStack>
            <Divider />
            {renderItemsForm(formState, setter)}
            <Divider />
            <Text fontWeight="bold">Courier Signature Block</Text>
            <HStack w="full" spacing={4} align="start">
                <FormControl><FormLabel>Name</FormLabel><Input value={formState.courierSignatureBlock?.name || ''} onChange={(e) => handleNestedChange(setter, 'courierSignatureBlock', 'name', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Date</FormLabel><Input type="date" value={formState.courierSignatureBlock?.date || ''} onChange={(e) => handleNestedChange(setter, 'courierSignatureBlock', 'date', e.target.value)} /></FormControl>
            </HStack>
            <FormControl><FormLabel>Signature</FormLabel><Textarea value={formState.courierSignatureBlock?.signature || ''} onChange={(e) => handleNestedChange(setter, 'courierSignatureBlock', 'signature', e.target.value)} /></FormControl>
            <Divider />
            <Text fontWeight="bold">Client Signature Block</Text>
            <HStack w="full" spacing={4} align="start">
                <FormControl><FormLabel>Name</FormLabel><Input value={formState.clientSignatureBlock?.name || ''} onChange={(e) => handleNestedChange(setter, 'clientSignatureBlock', 'name', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Date</FormLabel><Input type="date" value={formState.clientSignatureBlock?.date || ''} onChange={(e) => handleNestedChange(setter, 'clientSignatureBlock', 'date', e.target.value)} /></FormControl>
            </HStack>
            <FormControl><FormLabel>Signature</FormLabel><Textarea value={formState.clientSignatureBlock?.signature || ''} onChange={(e) => handleNestedChange(setter, 'clientSignatureBlock', 'signature', e.target.value)} /></FormControl>
            <FormControl><FormLabel>Notes</FormLabel><Textarea value={formState.notes || ''} onChange={(e) => handleChange(setter, 'notes', e.target.value)} /></FormControl>
        </VStack>
    );

    if (loading) return <Center mt="20%"><Loading /></Center>;

    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                {!error ? (
                    <>
                        <TitleHeader title="BSS COCs" buttonText={hasPermission('createBSSCOC') ? 'Create New COC' : undefined} onClick={hasPermission('createBSSCOC') ? onCreateOpen : undefined} />
                        <HStack spacing={4} mb="4" align="end">
                            <FormControl>
                                <FormLabel>Search</FormLabel>
                                <Input placeholder="Search by COC number, client, status" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </FormControl>
                            <Checkbox isChecked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}>Show deleted</Checkbox>
                        </HStack>

                        {filteredItems.length ? (
                            <Table variant="simple" size="sm">
                                <Thead bg={coolors.theadBg}>
                                    <Tr>
                                        <Th>#</Th>
                                        <Th cursor="pointer" onClick={() => toggleSort('cocNumber')}>COC Number {sortField === 'cocNumber' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}</Th>
                                        <Th cursor="pointer" onClick={() => toggleSort('clientName')}>Client {sortField === 'clientName' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}</Th>
                                        <Th>Items</Th>
                                        <Th>Status</Th>
                                        <Th textAlign="right">Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {filteredItems.map((item, index) => (
                                        <Tr key={item._id} borderBottom={`2px solid ${coolors.borderColor}`} bg={index % 2 === 0 ? coolors.rowBgEven : coolors.rowBgOdd} color={coolors.textColor} _hover={{ bg: coolors.rowHoverBg, color: 'white' }}>
                                            <Td><PopoverId place="BSS COCs" index={index + 1} content={item._id} /></Td>
                                            <Td>{item.cocNumber}</Td>
                                            <Td>{item?.clientId?.clientName || '-'}</Td>
                                            <Td>{item.items?.length || 0}</Td>
                                            <Td><Badge colorScheme={item.deletedAt ? 'red' : item.status === 'completed' ? 'green' : item.status === 'submitted' ? 'blue' : item.status === 'cancelled' ? 'red' : 'yellow'}>{item.deletedAt ? 'Deleted' : item.status}</Badge></Td>
                                            <Td textAlign="right">
                                                <HStack justifyContent="flex-end" spacing={2}>
                                                    {!item.deletedAt && hasPermission('updateBSSCOC') && <Button size="xs" colorScheme="blue" onClick={() => openEdit(item)}><EditIcon /></Button>}
                                                    {!item.deletedAt && hasPermission('deleteBSSCOC') && <Button size="xs" colorScheme="red" onClick={() => openDelete(item)}><DeleteIcon /></Button>}
                                                    {item.deletedAt && hasPermission('restoreBSSCOC') && <Button size="xs" colorScheme="green" onClick={() => handleRestore(item._id)}><RepeatIcon /></Button>}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        ) : <AccessDenied msg="No BSS COCs found!" icon="info" />}
                    </>
                ) : <AccessDenied msg={error} />}
            </VStack>

            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Confirm Deletion</ModalHeader>
                    <ModalBody>Are you sure you want to delete <strong>{selectedItem?.cocNumber}</strong>?</ModalBody>
                    <ModalFooter>
                        <Button colorScheme="red" mr={3} onClick={handleDelete}>Delete</Button>
                        <Button onClick={onDeleteClose}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" size="6xl" isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit COC</ModalHeader>
                    <ModalBody>{selectedItem && renderForm(selectedItem, setSelectedItem)}</ModalBody>
                    <ModalFooter>
                        <Button onClick={onClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleSave}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" size="6xl" isOpen={isCreateOpen} onClose={onCreateClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create COC</ModalHeader>
                    <ModalBody>{renderForm(newItem, setNewItem)}</ModalBody>
                    <ModalFooter>
                        <Button onClick={onCreateClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleCreate}>Create</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default BSSCOCsPage;